import {
    Cluster,
    GridAlgorithm,
    MarkerClusterer,
} from "@googlemaps/markerclusterer";
import { Wrapper } from "@googlemaps/react-wrapper";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOMServer from "react-dom/server";
import isEqual from "react-fast-compare";
import { ThemeProvider } from "styled-components";

import {
    useBreakpoint,
    useGoogleMapsKey,
    useMap,
    useSetMapOptions,
} from "hooks";
import { getMapsLocationUrl } from "utils/getMapsLocationUrl";

import {
    Container,
    InfoWindow,
    InfoWindowFindUs,
    InfoWindowHeading,
    InfoWindowText,
} from "./GoogleMap.styled";
import GoogleMapProps from "./GoogleMapProps";
import cluster56 from "./img/cluster56.svg"; // eslint-disable-line import/no-unresolved
import external28 from "./img/external28.svg"; // eslint-disable-line import/no-unresolved
import location105 from "./img/location105.svg"; // eslint-disable-line import/no-unresolved
import location140 from "./img/location140.svg"; // eslint-disable-line import/no-unresolved
import location56 from "./img/location56.svg"; // eslint-disable-line import/no-unresolved

const Map: React.FC<GoogleMapProps> = React.memo(
    ({ className, markers, options }: GoogleMapProps) => {
        type iconProps = {
            url: string;
            anchor: google.maps.Point;
        };
        const ref = useRef<HTMLDivElement>(null);
        const [map, setMap] = useState<google.maps.Map>();
        const [icon, setIcon] = useState<iconProps | undefined>(undefined);
        const [mapMarkers, setMapMarkers] = useState<
            (google.maps.Marker | undefined)[] | undefined
        >();

        const breakpoint = useBreakpoint();

        useMap(ref, setMap, map);
        useSetMapOptions(map, options);

        useEffect(() => {
            let anchorPoint: google.maps.Point;
            let iconUrl: string;

            switch (breakpoint) {
                case "XL":
                    iconUrl = location140;
                    anchorPoint = new google.maps.Point(70, 130);
                    break;
                case "L":
                case "M":
                    iconUrl = location105;
                    anchorPoint = new google.maps.Point(52.5, 96);
                    break;
                default:
                    iconUrl = location56;
                    anchorPoint = new google.maps.Point(28, 56);
            }
            const svgMarker = {
                url: iconUrl,
                anchor: anchorPoint,
            };
            setIcon(svgMarker);
        }, [breakpoint]);

        useEffect(() => {
            if (map && icon && markers) {
                if (mapMarkers)
                    mapMarkers?.forEach((marker) => marker?.setMap(null));
                setMapMarkers(
                    markers.map((markerOptions) => {
                        if (
                            markerOptions.position?.lng &&
                            markerOptions.position?.lat
                        ) {
                            const position = {
                                lat: markerOptions.position.lat,
                                lng: markerOptions.position.lng,
                            } as google.maps.LatLngLiteral;

                            const marker = new google.maps.Marker({
                                position,
                                map,
                                icon: icon,
                            });

                            return marker;
                        }
                    }),
                );
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [icon, map, markers]);

        return <Container className={className} ref={ref}></Container>;
    },
);
Map.displayName = "Map";

const MapWithCluster: React.FC<GoogleMapProps> = React.memo(
    ({ className, markers, options, l18n }: GoogleMapProps) => {
        const ref = useRef<HTMLDivElement>(null);
        const [map, setMap] = useState<google.maps.Map>();
        const [width, setWidth] = useState<number>(0);
        const [clusterer, setClusterer] = useState<MarkerClusterer>();
        const [infowindow, _setInfoWindow] = useState<google.maps.InfoWindow>(
            new google.maps.InfoWindow({}),
        );
        const [mapMarkers, setMapMarkers] = useState<google.maps.Marker[]>([]);
        const findUs = l18n?.findUs || "Find us";
        useMap(ref, setMap, map);
        useSetMapOptions(map, options);

        useEffect(() => {
            const updateMapWidth = () => {
                if (
                    map &&
                    ref.current?.clientWidth &&
                    ref.current.clientWidth > 0
                ) {
                    setWidth(ref.current?.clientWidth);
                }
            };

            if (map && width === 0) updateMapWidth();
            window.addEventListener("resize", updateMapWidth);

            return () => {
                window.removeEventListener("resize", updateMapWidth);
            };
        }, [map, width]);

        const addMarkers = useCallback(() => {
            if (clusterer) {
                const newMapMarkers: google.maps.Marker[] = [];
                infowindow.addListener("closeclick", () => {
                    // Handle focus manually.
                });

                markers?.forEach((options) => {
                    const InfoWindowContent = (
                        <ThemeProvider
                            theme={{
                                theme: "cyan",
                                contentTheme: "blue",
                                actionTheme: "blue",
                            }}
                        >
                            <InfoWindow>
                                {options.url && options.officeName && (
                                    <InfoWindowHeading
                                        href={options.url}
                                        title={options.officeName}
                                        $hoverSpan={true}
                                    >
                                        <span>{options.officeName}</span>
                                    </InfoWindowHeading>
                                )}
                                {options.addressRow1 && (
                                    <InfoWindowText>
                                        {options.addressRow1}
                                    </InfoWindowText>
                                )}
                                {(options.postalCode || options.city) && (
                                    <InfoWindowText>
                                        {[
                                            options.postalCode,
                                            options.city,
                                        ].join(" ")}
                                    </InfoWindowText>
                                )}
                                <InfoWindowFindUs
                                    href={getMapsLocationUrl(
                                        15,
                                        options.latitude,
                                        options.longitude,
                                        options.postalCode,
                                        options.city,
                                        options.addressRow1,
                                    )}
                                    title={options.officeName}
                                    $hoverSpan={true}
                                >
                                    <span>{findUs}</span>
                                    <img
                                        src={external28}
                                        alt=""
                                        aria-hidden="true"
                                    />
                                </InfoWindowFindUs>
                            </InfoWindow>
                        </ThemeProvider>
                    );

                    infowindow.setContent(
                        ReactDOMServer.renderToString(InfoWindowContent),
                    );

                    const marker = new google.maps.Marker({
                        ...options,
                        //map,
                        icon: {
                            url: location56,
                            anchor: new google.maps.Point(28, 56),
                        },
                    });

                    marker.addListener("click", () => {
                        infowindow.setContent(
                            ReactDOMServer.renderToString(InfoWindowContent),
                        );

                        infowindow.open({
                            anchor: marker,
                            map,
                        });
                    });

                    clusterer.addMarker(marker);
                    newMapMarkers.push(marker);
                });

                setMapMarkers(newMapMarkers);
            }
        }, [clusterer, findUs, infowindow, map, markers]);

        if (mapMarkers.length === 0) addMarkers();

        const removeMarkers = () => {
            if (clusterer) {
                mapMarkers &&
                    mapMarkers?.forEach((marker) => {
                        clusterer.removeMarker(marker);
                    });
                setMapMarkers([]);
            }
        };

        const centerMap = useCallback(() => {
            if (map && mapMarkers) {
                const bounds = new google.maps.LatLngBounds();
                mapMarkers.forEach((marker) => {
                    const position = marker.getPosition();
                    position && bounds.extend(position);
                });
                clusterer?.render();
                setTimeout(() => map.fitBounds(bounds), 500); // Wait half a second to fit the bounds to avoid frontend issue

                // Fix zoom bug where clusters are hidden above zoom 16.
                map.setZoom(Math.min(map.getZoom() || 16, 16));
            }
        }, [clusterer, map, mapMarkers]);

        useEffect(() => {
            if (map && !clusterer) {
                google.maps.event.addListener(map, "click", () => {
                    infowindow.close();
                });

                const renderer = {
                    render: ({ count, position }: Cluster) =>
                        new google.maps.Marker({
                            label: {
                                text: String(count),
                                className: "clusterLabel",
                            },
                            icon: {
                                url: cluster56,
                            },
                            position,
                            zIndex:
                                Number(google.maps.Marker.MAX_ZINDEX) + count,
                        }),
                };

                const clusterer = new MarkerClusterer({
                    map,
                    renderer,
                    algorithm: new GridAlgorithm({ gridSize: 45 }),
                });

                google.maps.event.addListener(clusterer, "click", () => {
                    infowindow.close(); // close the info window if a cluster is clicked.
                });

                setClusterer(clusterer);
            }
        }, [map, setClusterer, clusterer, infowindow]);

        useEffect(() => {
            if (markers) {
                removeMarkers(); // Check if it can be refactored to not force exhaustive-deps
                addMarkers();
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [addMarkers, markers]);

        useEffect(() => {
            if (mapMarkers) {
                centerMap();
            }
        }, [centerMap, mapMarkers]);

        return (
            <Container
                className={className}
                ref={ref}
                mapWidth={width}
            ></Container>
        );
    },
    isEqual,
);
MapWithCluster.displayName = "MapWithCluster";

const GoogleMap = ({
    className,
    markers,
    useCluster = false,
    options,
    l18n,
}: GoogleMapProps) => {
    const googleMapsKey = useGoogleMapsKey();

    return (
        <>
            {googleMapsKey ? (
                <Wrapper apiKey={googleMapsKey}>
                    {useCluster ? (
                        <MapWithCluster
                            className={className}
                            markers={markers}
                            useCluster={useCluster}
                            options={options}
                            l18n={l18n}
                        />
                    ) : (
                        <Map
                            className={className}
                            markers={markers}
                            useCluster={useCluster}
                            options={options}
                        />
                    )}
                </Wrapper>
            ) : (
                <></> //If no apiKey is provided, the map will not be rendered
            )}
        </>
    );
};

export default React.memo(GoogleMap, isEqual);
